import { Combobox } from "../~evaluate/QuestionSetCombobox";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Trash2Icon } from "lucide-react";
import {
  splitType,
  getValidTypeParts,
  formatLabels,
  marksLabels,
  categoryLabels,
} from "./quizTypeUtils";
import { CardTitle, CardDescription } from "@/components/ui/card";

interface QuizQuestionSpecProps {
  idx: number;
  form: import("react-hook-form").UseFormReturn<
    import("./quizSchemas").FormType
  >;
  questionTypes: string[];
  units: string[];
  topicsByUnit: Record<string, import("@/types").Topic[]>;
  remove: (idx: number) => void;
  handleUnitChange: (idx: number, value: string) => void;
  handleTypePartChange: (
    idx: number,
    part: "format" | "marks" | "category",
    value: string,
  ) => void;
}

export function QuizQuestionSpec({
  idx,
  form,
  questionTypes,
  units,
  topicsByUnit,
  remove,
  handleUnitChange,
  handleTypePartChange,
}: QuizQuestionSpecProps) {
  const selectedUnit = form.watch(`specs.${idx}.unit`);
  const topicOptions =
    selectedUnit && topicsByUnit[selectedUnit]
      ? topicsByUnit[selectedUnit].map((topic) => ({
          value: topic.id.toString(),
          label: topic.name.trim(),
        }))
      : [];
  const typeValue = form.watch(`specs.${idx}.type`) || "";
  const { format, marks, category } = splitType(typeValue);
  const validParts = getValidTypeParts(questionTypes, {
    format,
    marks,
    category,
  });

  return (
    <>
      <CardTitle className="pb-2 text-3xl font-bold">
        Question {idx + 1}
      </CardTitle>
      <CardDescription className="p-0">
        <div className="flex flex-col gap-6">
          <div className="flex w-full flex-col gap-4 md:flex-row md:items-end md:gap-6">
            <div className="min-w-[120px] flex-1">
              <FormLabel>Format</FormLabel>
              <FormControl>
                <Combobox
                  options={validParts.formats.map((f) => ({
                    value: f,
                    label: formatLabels[f] || f,
                  }))}
                  value={format}
                  setValue={(val) => handleTypePartChange(idx, "format", val)}
                  placeholder="Select format"
                  searchBoxPlaceholder="Search format..."
                  buttonClassName="w-full"
                />
              </FormControl>
            </div>
            <div className="min-w-[120px] flex-1">
              <FormLabel>Marks</FormLabel>
              <FormControl>
                <Combobox
                  options={validParts.marks.map((m) => ({
                    value: m,
                    label: marksLabels[m] || m,
                  }))}
                  value={marks}
                  setValue={(val) => handleTypePartChange(idx, "marks", val)}
                  placeholder="Select marks"
                  searchBoxPlaceholder="Search marks..."
                  buttonClassName="w-full"
                />
              </FormControl>
            </div>
            <div className="min-w-[120px] flex-1">
              <FormLabel>Category</FormLabel>
              <FormControl>
                <Combobox
                  options={validParts.categories.map((c) => ({
                    value: c,
                    label: categoryLabels[c] || c,
                  }))}
                  value={category}
                  setValue={(val) => handleTypePartChange(idx, "category", val)}
                  placeholder="Select category"
                  searchBoxPlaceholder="Search category..."
                  buttonClassName="w-full"
                />
              </FormControl>
            </div>
          </div>
          <div className="flex w-full flex-col gap-4 md:flex-row md:items-end md:gap-6">
            <div className="min-w-[120px] flex-1">
              <FormField
                control={form.control}
                name={`specs.${idx}.unit` as const}
                render={({ field: unitField }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Unit</FormLabel>
                    <FormControl>
                      <Combobox
                        options={units.map((unit) => ({
                          value: unit,
                          label: unit,
                        }))}
                        value={unitField.value}
                        setValue={(val) => handleUnitChange(idx, val)}
                        placeholder="Select unit"
                        searchBoxPlaceholder="Search units..."
                        buttonClassName="w-full"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="min-w-[120px] flex-1">
              <FormField
                control={form.control}
                name={`specs.${idx}.topic_id` as const}
                render={({ field: topicField }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Topic (optional)</FormLabel>
                    <FormControl>
                      <Combobox
                        options={topicOptions}
                        value={
                          topicField.value != null
                            ? topicField.value.toString()
                            : ""
                        }
                        setValue={(val) =>
                          topicField.onChange(
                            val !== undefined && val !== ""
                              ? Number(val)
                              : undefined,
                          )
                        }
                        placeholder={
                          selectedUnit
                            ? topicOptions.length > 0
                              ? "Select topic (optional)"
                              : "No topics in this unit"
                            : "Select a unit first"
                        }
                        searchBoxPlaceholder="Search topics..."
                        buttonClassName="w-full"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            <div className="mt-2 flex-none md:mt-0">
              <Button
                type="button"
                onClick={() => remove(idx)}
                variant="destructive"
                className="h-12 px-6"
              >
                Remove
                <Trash2Icon className="ml-2 size-4" />
              </Button>
            </div>
          </div>
        </div>
      </CardDescription>
    </>
  );
}
